import type { TFunction } from 'i18next'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { Drawer } from '@/components/ui/Drawer'
import { Checkbox, Field, Select } from '@/components/ui/Field'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { TabNav } from '@/components/ui/TabNav'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useSession } from '@/features/auth/api/useSession'
import { useRemoveLeg } from '@/features/cases/api/useCase'
import { useReplaceLegPoints } from '@/features/cases/api/useCaseLeg'
import { CarrierSelect } from '@/features/cases/components/CarrierSelect'
import { LegFields, type LegDraft } from '@/features/cases/components/CaseRecordSheets'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { useDirectionTemplates } from '@/features/settings/api/useDirectionTemplates'
import { directionNameTaken } from '@/features/settings/directionRules'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import type { CaseLeg } from '@/types/domain'

import {
  MAX_LEG_POINTS,
  chainEnds,
  chainFromEnds,
  chainFromStored,
  chainFromTemplate,
  chainProblems,
  effectiveTemplateName,
  emptyChainPoint,
  templateBlock,
  type ChainDraft,
  type ChainPointDraft,
  type TemplateNameDraft,
} from './legChain'
import { PointTimeline } from './PointTimeline'
import { RouteEditor } from './RouteEditor'
import { legIdentityHidden } from './identity'
import { chainWithReachedCount } from './routeChain'

/* «Параметры плеча» — every fact of a leg that is not a unit, in a drawer the
 * page opens on request (S-10, design record A3).
 *
 * ── «Готово» writes nothing ─────────────────────────────────────────────────
 *
 * The drawer edits a COPY of the page's draft and hands it back on «Готово»;
 * only the page's save bar writes. That is what lets one press of «Создать
 * плечо» carry the route, the leg and its units together — and what makes
 * closing the drawer with edits in it a question («Отменить изменения?») rather
 * than a loss.
 *
 * Two exceptions, both existing writes on a leg that already has a chain: the
 * arrival discs and «Изменить точки» write the chain immediately, in their own
 * editor, as they always have — an arrival is a report, not a draft. And
 * «Удалить плечо» deletes.
 *
 * ── «Маршрут» is the leg's ends ─────────────────────────────────────────────
 *
 * There are no «Откуда/Куда» inputs. On a leg with no stored points the tab is
 * the chain draft (`legChain.ts`): pick a template, edit the two ends, or add
 * points with days between them. Once a chain is stored its first and last
 * point are the ends, the timeline shows it, and a template only REPLACES it —
 * with the warning, because the arrivals go with it.
 *
 * ── «Транспорт» is the mode, the transshipment and the leg's carrier ────────
 *
 * The leg's OWN carrier (`case_legs.carrier_id`, #131 N-10) — the one a sea,
 * air or rail leg with no trucks is carried by, the one «Плечо целиком» reads
 * and the one a unit with no carrier of its own counts as. It is the same field
 * the unit card's «Новые строки:» edits; both move the draft, the save bar
 * writes it.
 *
 * Drawn only for a reader shown agent identity (`seesAgentIdentity`) on a leg
 * whose identity was not withheld from them — the gate the page uses. The
 * session is the gate rather than the leg's payload because `legs/new` has no
 * payload, and Sales edits the route: a picker keyed on the leg alone would be
 * live for them there. Absent rather than disabled, as on the unit card: the
 * server would keep the stored carrier whatever was sent (UpdateLeg), and
 * Sales is not told the field exists (AD-6).
 */

export type LegParamsTab = 'route' | 'transport' | 'documents' | 'dates' | 'status'

/** What the drawer edits — the page's draft, as a copy. */
export interface LegParamsValue {
  fields: LegDraft
  /** The chain draft of a leg with no stored points; null on one with. */
  chain: ChainDraft | null
  /** The template to apply over a stored chain at the save, `''` for none. */
  templateApply: string
  /** «Сохранить как шаблон», ticked with this name; null unticked. */
  saveAsTemplate: TemplateNameDraft | null
}

export function LegParamsDrawer({
  open,
  onClose,
  tab: initialTab,
  value,
  onDone,
  caseId,
  leg,
  readOnly,
  readOnlyReason,
  canManageTemplates,
  onDeleted,
}: {
  open: boolean
  onClose: () => void
  tab: LegParamsTab
  value: LegParamsValue
  onDone: (next: LegParamsValue) => void
  caseId: string
  /** The saved leg; absent on `legs/new`. */
  leg?: CaseLeg
  readOnly: boolean
  /** Why the drawer is read-only, said at its top. */
  readOnlyReason?: string
  /** `route.template.manage` (or the superuser): «+ Новый» and «Сохранить как
   *  шаблон» exist at all. */
  canManageTemplates: boolean
  onDeleted: () => void
}) {
  const { t } = useTranslation()
  const { data: session } = useSession()
  /* The page's own identity gate (CaseLegPage): the session says the reader is
     shown carriers, and the leg — when there is one — did not arrive with any
     identity withheld. Fails closed on both. */
  const identityShown =
    session?.seesAgentIdentity === true && !(leg !== undefined && legIdentityHidden(leg))
  const [draft, setDraft, , , dirty] = useFormDraft<LegParamsValue>(open ? 'open' : null, () => value)
  const [tab, setTab] = useState<LegParamsTab>(initialTab)
  const [openedFor, setOpenedFor] = useState(open)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  if (openedFor !== open) {
    setOpenedFor(open)
    if (open) {
      setTab(initialTab)
      setConfirmingDiscard(false)
    }
  }

  const { data: activeTemplates } = useDirectionTemplates({ activeOnly: true })
  /* The UNFILTERED list for the name check: a retired template still owns its
     name (0053's index covers active and inactive alike). */
  const { data: allTemplates } = useDirectionTemplates()

  const storedPoints = leg?.points ?? []
  const hasChain = storedPoints.length > 0
  const chain = draft.chain
  const problems = chain ? chainProblems(chain) : undefined

  /* The points a ticked «Сохранить как шаблон» would keep: the draft's chain,
     or the stored one on a leg that has it. */
  const templatePoints: ChainPointDraft[] = chain ? chain.points : chainFromStored(storedPoints)
  const block = templateBlock(templatePoints)
  /* The name follows the chain («<first> — <last>») until the reader types
     their own. */
  const templateName = draft.saveAsTemplate
    ? effectiveTemplateName(draft.saveAsTemplate, templatePoints)
    : ''
  const nameTaken = directionNameTaken(allTemplates ?? [], templateName)

  const doneBlocked = problems?.invalid
    ? t('common.form.fixFields')
    : draft.saveAsTemplate && block === null && templateName === ''
      ? t('cases.legParams.templateNameRequired')
      : draft.saveAsTemplate && block === null && nameTaken
        ? t('cases.legParams.templateNameTaken')
        : undefined

  const setFields = (fields: LegDraft) => setDraft({ ...draft, fields })
  const setChain = (next: ChainDraft) => {
    const ends = chainEnds(next)
    setDraft({ ...draft, chain: next, fields: { ...draft.fields, ...ends } })
  }
  const editPoint = (key: string, patch: Partial<ChainPointDraft>) => {
    if (!chain) return
    setChain({
      ...chain,
      fromTemplate: false,
      points: chain.points.map((point) => (point.key === key ? { ...point, ...patch } : point)),
    })
  }

  const requestClose = () => {
    if (dirty && !readOnly) {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  const endsMissing =
    !hasChain && (draft.fields.origin.trim() === '' || draft.fields.destination.trim() === '')
  const modeMissing = draft.fields.transportModeId === ''

  const tabs = [
    {
      key: 'route' as const,
      labelKey: 'cases.legParams.tabs.route' as const,
      ...(endsMissing ? { badge: true as const, badgeTone: 'attention' as const, badgeLabel: t('cases.legParams.endsMissing') } : {}),
    },
    {
      key: 'transport' as const,
      labelKey: 'cases.legParams.tabs.transport' as const,
      ...(modeMissing ? { badge: true as const, badgeTone: 'attention' as const, badgeLabel: t('cases.legParams.modeMissing') } : {}),
    },
    { key: 'documents' as const, labelKey: 'cases.legParams.tabs.documents' as const },
    { key: 'dates' as const, labelKey: 'cases.legParams.tabs.dates' as const },
    { key: 'status' as const, labelKey: 'cases.legParams.tabs.status' as const },
  ]

  return (
    <Drawer
      open={open}
      onClose={requestClose}
      title={t('cases.legParams.title')}
      size="wide"
      footer={
        readOnly ? (
          <Button variant="secondary" className="ml-auto" onClick={onClose}>
            {t('common.actions.close')}
          </Button>
        ) : confirmingDiscard ? (
          <>
            <span className="text-text-body min-w-0 flex-1 text-[13px] leading-snug">
              {t('cases.legParams.discardConfirm')}
            </span>
            <Button variant="secondary" onClick={() => setConfirmingDiscard(false)}>
              {t('cases.legParams.keepEditing')}
            </Button>
            <Button variant="danger" onClick={onClose}>
              {t('cases.legParams.discard')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" className="ml-auto" onClick={requestClose}>
              {t('common.actions.cancel')}
            </Button>
            <Button blockedReason={doneBlocked} onClick={() => onDone(draft)}>
              {t('cases.legParams.done')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4 px-4 pb-5">
        <TabNav<LegParamsTab>
          items={tabs}
          value={tab}
          onChange={setTab}
          label={t('cases.legParams.tabsLabel')}
          className="-mx-4 px-2"
        />

        {readOnlyReason ? (
          <p className="bg-surface-sunken text-text-muted rounded-md px-3 py-2 text-[12.5px] leading-snug">
            {readOnlyReason}
          </p>
        ) : null}

        {tab === 'route' ? (
          <div className="flex flex-col gap-4">
            {hasChain ? (
              <StoredRoute
                caseId={caseId}
                leg={leg!}
                readOnly={readOnly}
                templateApply={draft.templateApply}
                onTemplateApply={(templateApply) => setDraft({ ...draft, templateApply })}
                templates={activeTemplates ?? []}
              />
            ) : chain ? (
              <>
                <div className="flex items-end gap-2">
                  <Field label={t('cases.legPage.directionTemplate')} className="min-w-0 flex-1">
                    {(props) => (
                      <Select
                        value={chain.templateId}
                        onChange={(id) => {
                          const picked = (activeTemplates ?? []).find((template) => template.id === id)
                          if (picked) setChain(chainFromTemplate(picked))
                        }}
                        options={(activeTemplates ?? []).map((template) => ({
                          value: template.id,
                          label: template.name,
                          hint: t('settings.directions.pointCount', { count: template.points.length }),
                        }))}
                        placeholder={t('cases.legPage.directionTemplateNone')}
                        disabled={readOnly}
                        {...props}
                      />
                    )}
                  </Field>
                  {canManageTemplates && !readOnly ? (
                    /* A new template starts as the two current ends and is
                       created only by «Сохранить как шаблон» at the save. */
                    <Button
                      variant="ghost"
                      iconLeft={Plus}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          chain: chainFromEnds(chainEnds(chain)),
                          saveAsTemplate: { name: '', edited: false },
                        })
                      }
                    >
                      {t('cases.legParams.newTemplate')}
                    </Button>
                  ) : null}
                </div>

                <ChainEditor
                  chain={chain}
                  readOnly={readOnly}
                  problems={problems}
                  onEdit={editPoint}
                  onChange={setChain}
                />
              </>
            ) : null}

            {canManageTemplates && !readOnly ? (
              <div className="border-line-hair flex flex-col gap-2 border-t pt-3">
                <Checkbox
                  label={t('cases.legParams.saveAsTemplate')}
                  checked={draft.saveAsTemplate !== null && block === null}
                  disabled={block !== null}
                  /* The reason as prose beside the box — a disabled control
                     whose why is on screen (frontend/README). */
                  hint={
                    block === 'noDays'
                      ? t('cases.legParams.templateNoDays')
                      : block === 'nameTooLong'
                        ? t('cases.legParams.templateNameTooLong')
                        : undefined
                  }
                  onCheckedChange={(checked) =>
                    setDraft({
                      ...draft,
                      saveAsTemplate: checked ? { name: '', edited: false } : null,
                    })
                  }
                />
                {draft.saveAsTemplate && block === null ? (
                  <Field
                    label={t('cases.legParams.templateName')}
                    error={
                      templateName === ''
                        ? t('common.form.required')
                        : nameTaken
                          ? t('cases.legParams.templateNameTaken')
                          : undefined
                    }
                  >
                    {(props) => (
                      <Input
                        value={draft.saveAsTemplate?.edited ? draft.saveAsTemplate.name : templateName}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            saveAsTemplate: { name: event.target.value, edited: true },
                          })
                        }
                        {...props}
                      />
                    )}
                  </Field>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-3.5">
            <LegFields
              draft={draft.fields}
              setDraft={setFields}
              touched={false}
              record={leg}
              actuals={leg !== undefined}
              section={tab}
              readOnly={readOnly}
            />
            {tab === 'transport' && identityShown ? (
              /* Both halves move together: a pick replaces the id AND the name,
                 so the stored free text of a pre-0046 leg never rides along
                 with a new link. Optional — no badge when it is empty. */
              <Field label={t('cases.legs.carrier')} hint={t('cases.legParams.carrierHint')}>
                {(props) => (
                  <CarrierSelect
                    value={draft.fields.carrierId}
                    name={draft.fields.carrierName}
                    disabled={readOnly}
                    onChange={(carrier) =>
                      setFields({ ...draft.fields, carrierId: carrier.id, carrierName: carrier.name })
                    }
                    {...props}
                  />
                )}
              </Field>
            ) : null}
            {tab === 'status' && leg && !readOnly ? (
              <DeleteLeg caseId={caseId} legId={leg.id} onDeleted={onDeleted} />
            ) : null}
          </div>
        )}
      </div>
    </Drawer>
  )
}

/** The chain of a leg with no stored points: one row per point, the days INTO
 *  a point typed on the line above it — read down, «Khorgos, 2 дн, Нур-Жол». */
function ChainEditor({
  chain,
  readOnly,
  problems,
  onEdit,
  onChange,
}: {
  chain: ChainDraft
  readOnly: boolean
  problems?: ReturnType<typeof chainProblems>
  onEdit: (key: string, patch: Partial<ChainPointDraft>) => void
  onChange: (chain: ChainDraft) => void
}) {
  const { t } = useTranslation()
  const last = chain.points.length - 1

  return (
    <div className="flex flex-col gap-1">
      <ol className="flex flex-col">
        {chain.points.map((point, index) => {
          const nameKey = problems?.name.get(point.key)
          const dayKey = problems?.days.get(point.key)
          return (
            <li key={point.key} className="flex flex-col">
              {index > 0 ? (
                <div className="flex items-start gap-2 py-1 pl-[7px]">
                  <span aria-hidden className="bg-line mt-0 h-9 w-px shrink-0" />
                  <div className="ml-3 flex flex-col gap-1">
                    <span className="flex items-center gap-1.5">
                      <Input
                        inputSize="sm"
                        inputMode="numeric"
                        className="asa-num w-16"
                        value={point.days}
                        disabled={readOnly}
                        aria-label={t('settings.directions.pointDays', { n: index + 1 })}
                        aria-invalid={dayKey !== undefined || undefined}
                        onChange={(event) => onEdit(point.key, { days: event.target.value })}
                      />
                      <span className="text-text-muted text-[12.5px]">{t('cases.legParams.daysUnit')}</span>
                    </span>
                    {dayKey ? (
                      <span className="text-negative text-[11.5px] leading-snug">{t(dayKey)}</span>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <div className="flex items-start gap-2">
                <span
                  aria-hidden
                  className={cn(
                    'border-text-heading mt-2 size-[15px] shrink-0 rounded-full border-2',
                    (index === 0 || index === last) && 'bg-surface',
                  )}
                />
                <CountryDropdown
                  flagOnly
                  value={point.country}
                  disabled={readOnly}
                  onChange={(country) => onEdit(point.key, { country })}
                  aria-label={t('settings.directions.pointCountry', { n: index + 1 })}
                  className="w-auto shrink-0 px-2.5"
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <Input
                    value={point.name}
                    disabled={readOnly}
                    aria-label={
                      index === 0
                        ? t('cases.legParams.origin')
                        : index === last
                          ? t('cases.legParams.destination')
                          : t('settings.directions.pointName', { n: index + 1 })
                    }
                    placeholder={t('settings.directions.pointPlaceholder')}
                    aria-invalid={nameKey !== undefined || undefined}
                    onChange={(event) => onEdit(point.key, { name: event.target.value })}
                  />
                  {nameKey ? (
                    <span className="text-negative text-[11.5px] leading-snug">{t(nameKey)}</span>
                  ) : null}
                </div>
                {!readOnly && chain.points.length > 2 ? (
                  <IconButton
                    icon={Trash2}
                    size="sm"
                    className="mt-0.5"
                    label={t('settings.directions.removePoint', { n: index + 1 })}
                    onClick={() =>
                      onChange({
                        ...chain,
                        fromTemplate: false,
                        points: chain.points.filter((other) => other.key !== point.key),
                      })
                    }
                  />
                ) : null}
              </div>
            </li>
          )
        })}
      </ol>

      {readOnly ? null : chain.points.length >= MAX_LEG_POINTS ? (
        <span className="text-text-muted text-[12px] leading-snug font-light">
          {t('cases.legPage.routeEditTooMany', { max: MAX_LEG_POINTS })}
        </span>
      ) : (
        /* A stop between the ends — inserted before the destination, because a
           point added after it would silently move where the leg goes. */
        <Button
          variant="ghost"
          size="sm"
          iconLeft={Plus}
          className="self-start"
          onClick={() =>
            onChange({
              ...chain,
              fromTemplate: false,
              points: [...chain.points.slice(0, -1), emptyChainPoint(), ...chain.points.slice(-1)],
            })
          }
        >
          {t('cases.legParams.addPoint')}
        </Button>
      )}
      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('cases.legParams.chainNote')}
      </p>
    </div>
  )
}

/** A leg with a stored chain: its timeline and arrival discs, its own editor
 *  (both immediate writes, as before), and a template that would replace it. */
function StoredRoute({
  caseId,
  leg,
  readOnly,
  templateApply,
  onTemplateApply,
  templates,
}: {
  caseId: string
  leg: CaseLeg
  readOnly: boolean
  templateApply: string
  onTemplateApply: (templateId: string) => void
  templates: { id: string; name: string; points: unknown[] }[]
}) {
  const { t } = useTranslation()
  const replacePoints = useReplaceLegPoints(caseId, leg.id)
  const [editingPoints, setEditingPoints] = useState(false)
  const points = leg.points ?? []

  if (editingPoints) {
    return (
      <RouteEditor
        legId={leg.id}
        points={points}
        pending={replacePoints.isPending}
        error={replacePoints.error ? pointsRefusal(replacePoints.error, t) : undefined}
        onCancel={() => {
          replacePoints.reset()
          setEditingPoints(false)
        }}
        onSubmit={(payload) =>
          replacePoints.mutate(payload, { onSuccess: () => setEditingPoints(false) })
        }
      />
    )
  }

  return (
    <>
      <Field label={t('cases.legPage.directionTemplate')}>
        {(props) => (
          <Select
            value={templateApply}
            onChange={onTemplateApply}
            options={[
              { value: '', label: t('cases.legPage.directionTemplateNone') },
              ...templates.map((template) => ({
                value: template.id,
                label: template.name,
                hint: t('settings.directions.pointCount', { count: template.points.length }),
              })),
            ]}
            disabled={readOnly}
            {...props}
          />
        )}
      </Field>
      {/* NOTICE, not error: nothing has gone wrong; this is what the save will
          do to the arrivals recorded on this chain. */}
      {templateApply !== '' ? (
        <Banner tone="attention" title={t('cases.legPage.applyTemplateWarning')} />
      ) : null}

      <PointTimeline
        points={points}
        currentPointId={leg.currentPointId}
        nextPointDueAt={leg.nextPointDueAt}
        status={leg.status}
        pending={replacePoints.isPending}
        onMarkThrough={
          readOnly ? undefined : (count) => replacePoints.mutate(chainWithReachedCount(points, count))
        }
      />
      {replacePoints.error ? (
        <p role="alert" className="text-negative text-[12.5px] leading-snug">
          {pointsRefusal(replacePoints.error, t)}
        </p>
      ) : null}
      {readOnly ? null : (
        <Button variant="secondary" size="sm" className="self-start" onClick={() => setEditingPoints(true)}>
          {t('cases.legParams.editPoints')}
        </Button>
      )}
    </>
  )
}

/** «Удалить плечо», behind two presses. The refusal is the control (FR-67): a
 *  leg with costs cannot be deleted and the server names them. */
function DeleteLeg({
  caseId,
  legId,
  onDeleted,
}: {
  caseId: string
  legId: string
  onDeleted: () => void
}) {
  const { t } = useTranslation()
  const remove = useRemoveLeg(caseId)
  const [confirming, setConfirming] = useState(false)

  return (
    <div className="border-line-hair mt-2 flex flex-col gap-2 border-t pt-3">
      {remove.error ? (
        <p role="alert" className="text-negative text-[12.5px] leading-snug">
          {refusalMessage(remove.error, t)}
        </p>
      ) : null}
      {confirming ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-text-body flex-1 text-[12.5px] leading-snug">
            {t('cases.legs.deleteConfirm')}
          </span>
          <Button variant="secondary" size="sm" disabled={remove.isPending} onClick={() => setConfirming(false)}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={remove.isPending}
            onClick={() => remove.mutate(legId, { onSuccess: onDeleted })}
          >
            {t('cases.legs.deleteConfirmAction')}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="text-negative cursor-pointer self-start text-[12.5px]"
        >
          {t('cases.legs.delete')}
        </button>
      )}
    </div>
  )
}

/** A refusal from the chain write, in words. The prefix violation gets its own
 *  sentence: it is the one a reader can act on («mark the earlier point»). */
function pointsRefusal(error: unknown, t: TFunction): string {
  if (error instanceof ApiError) {
    const fields = error.problem.errors ?? {}
    const notContiguous = Object.entries(fields).some(
      ([field, codes]) => field.endsWith('.reached') && codes.includes('not-contiguous'),
    )
    if (notContiguous) return t('cases.legPage.notContiguous')
  }
  return refusalMessage(error, t)
}
